import type React from 'react'
import type { JSX } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { ITVShowPopupProps } from 'blocks/Popup/Templates'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ButtonFonts } from 'atoms/Button'
import { InlinePrimaryBaseTemplate } from 'atoms/Button/Templates'
import { TextBox, TextBoxColors, TextBoxTypes } from 'atoms/TextBox'
import { ShowMorePositions } from 'blocks/TextBlock/TextBlock.h'
import {
  StrokeHeightChecker,
  TextBlockWithGradientSC,
  TextBlockWithShowMoreSC,
} from 'blocks/TextBlock/TextBlock.styled'
import {
  DetailsAreaColumnSC,
  DetailsAreaContentSC,
  DetailsAreaPeopleSC,
  DetailsAreaTextSC,
} from './TextBlockWithMore.styled'

/**
 * Decides whether the details area needs its More Details button.
 *
 * The concept hides the button while description, directors and stars all fit
 * the `maxLines` the block shows, and shows it as soon as one of them needs
 * more. A value of exactly `maxLines + 1` lines is already ellipsised, so it
 * counts as clipped and brings the button back.
 */
export const isDetailsAreaClipped = (
  lineCounts: readonly number[],
  maxLines: number
): boolean => lineCounts.some((lines) => lines > maxLines)

const linesOf = (element: HTMLElement | null, lineHeight: number): number =>
  element && lineHeight > 0 ? Math.round(element.scrollHeight / lineHeight) : 0

export interface ITextBlockWithMoreProps {
  children?: React.ReactNode
  description: string
  stars: string
  directors: string
  maxLines?: number
  width?: number
  showMorePosition?: ShowMorePositions
  font?: TextBoxTypes
  fontDirectors?: TextBoxTypes
  onClickMore?: (args: any) => void
  color?: ITextBoxProps['color']
  episodePopup?: ITVShowPopupProps['title']
  titlePopup?: ITVShowPopupProps['description']
  labelsPopup?: ITVShowPopupProps['labels']
  listPopup?: ITVShowPopupProps['list']
  starsPopup?: ITVShowPopupProps['stars']
  directorsPopup?: ITVShowPopupProps['directors']
  titleButton?: string
  fullInformation?: boolean
  withUsingPortal?: boolean
  popupWidth?: string
  textInBanner?: boolean
  maxLineDirectors?: number
  maxLineStars?: number
  directorsTitle?: string
  starsTitle?: string
  descriptionTitle?: string
  /** Renders the block as the details page area that follows the banner. */
  detailsArea?: boolean
  /**
   * Whether the `detailsArea` layout may reserve a column for directors/stars.
   * Defaults to `true` for concepts (movie/TV show) that show a people column
   * beside a description. The reserved empty track is used only while a
   * description is present so a people-only title still spans the full row.
   * Concepts that never render a people column, such as the season
   * description, pass `false` so the description keeps the full width of its
   * own container instead of half of it.
   */
  hasPeopleColumn?: boolean
}

export const TextBlockWithMore = ({
  description,
  stars,
  directors,
  maxLines = 3,
  width,
  showMorePosition = ShowMorePositions.Inline,
  font = TextBoxTypes.Body3,
  onClickMore,
  color = TextBoxColors.TEXT_ICON_PRIMARY,
  children: _children,
  titleButton = 'More',
  fullInformation = true,
  fontDirectors = TextBoxTypes.Body3,
  textInBanner,
  maxLineDirectors,
  maxLineStars,
  directorsTitle = 'Directors',
  starsTitle = 'Stars',
  descriptionTitle,
  detailsArea = false,
  hasPeopleColumn = true,
}: ITextBlockWithMoreProps): JSX.Element => {
  const [isOverflow, setIsOverflow] = useState<boolean>()
  const [maxHeight, setMaxHeight] = useState<number | undefined>()
  const [lineHeight, setLineHeight] = useState<number | undefined>()
  const [btnWidth, setBtnWidth] = useState<number | undefined>()
  const blockRef = useRef<HTMLDivElement>(null)
  const strokeRef = useRef<HTMLDivElement>(null)
  const descriptionRef = useRef<HTMLSpanElement>(null)
  const directorsRef = useRef<HTMLSpanElement>(null)
  const starsRef = useRef<HTMLSpanElement>(null)
  const pendingOverflowRafRef = useRef<number[]>([])
  const [clientWidth, setClientWidth] = useState(0)

  const childrenHeight = useCallback((): number => {
    return (
      (descriptionRef.current?.scrollHeight || 0) +
      (directorsRef.current?.scrollHeight || 0) +
      (starsRef.current?.scrollHeight || 0)
    )
  }, [])

  const cancelScheduledOverflowUpdate = useCallback(() => {
    for (const rafId of pendingOverflowRafRef.current) {
      cancelAnimationFrame(rafId)
    }
    pendingOverflowRafRef.current = []
  }, [])

  const updateIsOverflow = useCallback(() => {
    if (!blockRef.current || !strokeRef.current) return

    strokeRef.current.innerHTML = titleButton
    const { height: measuredLineHeight, width: measuredBtnWidth } =
      strokeRef.current.getBoundingClientRect()
    setLineHeight(measuredLineHeight)
    setBtnWidth(measuredBtnWidth)
    const maxBlockHeight = measuredLineHeight * maxLines
    setMaxHeight(maxBlockHeight)
    setIsOverflow(
      detailsArea
        ? isDetailsAreaClipped(
            [descriptionRef, directorsRef, starsRef].map(({ current }) =>
              linesOf(current, measuredLineHeight)
            ),
            maxLines
          )
        : childrenHeight() > maxBlockHeight
    )
    strokeRef.current.innerHTML = ''
  }, [childrenHeight, detailsArea, maxLines, titleButton])

  const scheduleOverflowUpdate = useCallback(() => {
    cancelScheduledOverflowUpdate()

    const outerRafId = requestAnimationFrame(() => {
      const innerRafId = requestAnimationFrame(() => {
        pendingOverflowRafRef.current = []
        updateIsOverflow()
      })
      pendingOverflowRafRef.current = [innerRafId]
    })
    pendingOverflowRafRef.current = [outerRafId]
  }, [cancelScheduledOverflowUpdate, updateIsOverflow])

  useEffect(() => {
    const onViewportChange = () => {
      setClientWidth(window.innerWidth)
    }

    setClientWidth(window.innerWidth)

    window.addEventListener('resize', onViewportChange)
    window.addEventListener('orientationchange', onViewportChange)

    return () => {
      window.removeEventListener('resize', onViewportChange)
      window.removeEventListener('orientationchange', onViewportChange)
    }
  }, [])

  useEffect(() => {
    scheduleOverflowUpdate()
  }, [maxLines, font, description, directors, stars, scheduleOverflowUpdate])

  useEffect(() => {
    const block = blockRef.current
    if (!block) return

    const resizeObserver = new ResizeObserver(() => {
      scheduleOverflowUpdate()
    })

    resizeObserver.observe(block)

    return () => {
      resizeObserver.disconnect()
    }
  }, [scheduleOverflowUpdate])

  useEffect(() => {
    return () => {
      cancelScheduledOverflowUpdate()
    }
  }, [cancelScheduledOverflowUpdate])

  const isTablet = clientWidth < 1240
  const showContent = useMemo(() => {
    if (textInBanner && !description) {
      return fullInformation
    }
    if (textInBanner && description) {
      if (isTablet) {
        return fullInformation
      }
      return false
    }
    return fullInformation || (isTablet && !description)
  }, [isTablet, description, fullInformation, textInBanner])

  if (detailsArea) {
    return (
      <DetailsAreaContentSC>
        <DetailsAreaTextSC
          ref={blockRef}
          hasPeopleColumn={hasPeopleColumn && Boolean(description)}
        >
          {description && (
            <DetailsAreaColumnSC>
              {descriptionTitle && (
                <TextBox
                  title={descriptionTitle}
                  font={font}
                  color={TextBoxColors.TEXT_ICON_SECONDARY}
                />
              )}
              <TextBox
                ref={descriptionRef}
                title={description}
                font={font}
                color={color}
                maxLines={maxLines}
                preWrap
              />
            </DetailsAreaColumnSC>
          )}

          {(directors || stars) && (
            <DetailsAreaPeopleSC>
              {directors && (
                <DetailsAreaColumnSC>
                  <TextBox
                    title={directorsTitle}
                    font={fontDirectors}
                    color={TextBoxColors.TEXT_ICON_SECONDARY}
                  />
                  <TextBox
                    ref={directorsRef}
                    title={directors}
                    font={font}
                    color={color}
                    maxLines={maxLineDirectors || maxLines}
                  />
                </DetailsAreaColumnSC>
              )}

              {stars && (
                <DetailsAreaColumnSC>
                  <TextBox
                    title={starsTitle}
                    font={fontDirectors}
                    color={TextBoxColors.TEXT_ICON_SECONDARY}
                  />
                  <TextBox
                    ref={starsRef}
                    title={stars}
                    font={font}
                    color={color}
                    maxLines={maxLineStars || maxLines}
                  />
                </DetailsAreaColumnSC>
              )}
            </DetailsAreaPeopleSC>
          )}
        </DetailsAreaTextSC>
        {isOverflow && onClickMore && (
          <InlinePrimaryBaseTemplate
            title={titleButton}
            font={ButtonFonts.FontSmBold}
            onClick={onClickMore}
          />
        )}
        <StrokeHeightChecker font={font} ref={strokeRef} />
      </DetailsAreaContentSC>
    )
  }

  return (
    <>
      <TextBlockWithShowMoreSC
        width={width!}
        showMorePosition={showMorePosition}
        colorDefault={color}
      >
        <TextBlockWithGradientSC
          maxLines={maxLines}
          ref={blockRef}
          showMorePosition={showMorePosition}
          showOverflow={isOverflow}
          maxHeight={maxHeight}
          lineHeight={lineHeight}
          btnWidth={btnWidth}
        >
          {description && (
            <TextBox
              ref={descriptionRef}
              title={description}
              font={font}
              maxLines={isOverflow ? maxLines : maxLines + 1}
              preWrap
            >
              {description}
            </TextBox>
          )}

          {showContent && directors && (
            <TextBox maxLines={maxLineDirectors || maxLines} ref={directorsRef}>
              <TextBox
                title={directorsTitle}
                font={fontDirectors}
                color={TextBoxColors.TEXT_ICON_SECONDARY}
              />
              <TextBox title={directors} font={font} />
            </TextBox>
          )}
          {showContent && stars && (
            <TextBox maxLines={maxLineStars || maxLines} ref={starsRef}>
              <TextBox
                title={starsTitle}
                font={fontDirectors}
                color={TextBoxColors.TEXT_ICON_SECONDARY}
              />
              <TextBox title={stars} font={font} />
            </TextBox>
          )}
        </TextBlockWithGradientSC>
        {isOverflow && onClickMore && (
          <InlinePrimaryBaseTemplate
            title={titleButton}
            onClick={isOverflow && onClickMore}
          />
        )}
        <StrokeHeightChecker font={font} ref={strokeRef} />
      </TextBlockWithShowMoreSC>
    </>
  )
}
